<template>
  <div v-if="pollData !== null" class="box info-box content proposal">
    <h1 class="has-text-weight-medium">{{ pollData.proposal.title }}</h1>

    <div class="field is-grouped is-grouped-multiline">
      <div class="control">
        <div class="tags has-addons">
          <span class="tag is-dark">Proposed By</span>
          <span class="tag is-primary">
            <a class="has-text-white" target="_blank" rel="noopener" :href="tzktLink(pollData.author)">
              {{ pollData.author }}
            </a>
          </span>
        </div>
      </div>
      <div class="control">
        <div class="tags has-addons">
          <span class="tag is-dark">Proposed In Block</span>
          <span class="tag is-primary">
            <a class="has-text-white" target="_blank" rel="noopener" :href="`${$store.tzstatsURL}/${pollData.votingStartBlock}`">
              {{ pollData.votingStartBlock.toNumber().toLocaleString() }}
            </a>
          </span>
        </div>
      </div>
      <slot class="extra-tags" name="extra-tags"></slot>
    </div>

    <slot name="extra-header"></slot>

    <p v-if="!descHashMatches" class="has-text-danger"><b>Warning!</b> This content displayed below doesn't match the hash submitted with the proposal, which means this proposal's description has changed for some reason. </p>

    <div v-if="pollDescriptionError !== null" class="content proposal-description has-text-danger has-text-weight-bold" v-text="pollDescriptionError"></div>
    <div v-else-if="pollDescription !== null" class="content proposal-description" v-html="pollDescription"></div>
    <div v-else class="content proposal-description">
      <div class="loader"></div>
    </div>

    <michelson-editor
      v-if="pollData.proposal.proposalLambda"
      :michelson-code="pollData.proposal.proposalLambda"
    />

    <hr>

    <div class="voting-info">
      <div class="vote-status">
        <h4 class="has-text-weight-medium">
          Vote Status: {{ currentVoteStatus }}
          <span v-if="currentVoteIsOpen" class="loader is-inline-block is-small"></span>
        </h4>
        <progress :class="{'is-primary': !currentVoteIsOpen, 'is-grey': currentVoteIsOpen}" class="progress is-marginless is-small" :value="$store.currentBlockHeight - pollData.votingStartBlock.toNumber()" :max="pollData.votingEndBlock.minus(pollData.votingStartBlock).toNumber()"></progress>
        <div class="progress-footer">
          <p class="heading is-marginless">
            Opened
            <b>{{ calculateBlockTime(pollData.votingStartBlock) }}</b> ago (block
            <a rel="noopener" target="_blank" :href="`${$store.tzstatsURL}/${pollData.votingStartBlock}`">
              {{ pollData.votingStartBlock.toLocaleString() }}
            </a>
            )
          </p>
          <p class="heading is-marginless">
            <span v-if="currentVoteIsOpen">Ending in </span>
            <span v-else>Ended </span>
            <b>{{ calculateBlockTime(pollData.votingEndBlock) }}</b> <span v-if="!currentVoteIsOpen">ago</span> (block
            <a rel="noopener" target="_blank" :href="`${$store.tzstatsURL}/${pollData.votingEndBlock}`">
              {{ pollData.votingEndBlock.toLocaleString() }}
            </a>
            )
          </p>
        </div>
        <h4 class="has-text-weight-medium">
          Quorum Status:
          <b>
            <span v-if="quorumHasBeenMet" class="has-text-primary">Quorum Met</span>
            <span v-else class="has-text-danger">Quorum Not Met</span>
          </b>
        </h4>
        <progress :class="{'is-primary': quorumHasBeenMet, 'is-grey': !quorumHasBeenMet}" class="progress is-marginless is-small" :value="quorumProgress" max="100"></progress>
        <div class="progress-footer">
          <p class="heading is-marginless">
            Quorum Requires: {{ parseFloat(pollData.quorum.dividedBy(Math.pow(10, 18)).toFixed(2)).toLocaleString() }} kDAO | Current: {{ parseFloat(pollData.totalVotes.dividedBy(Math.pow(10, 18)).toFixed(2)).toLocaleString() }} kDAO
          </p>
          <p class="heading is-marginless">
            Progress: {{ quorumProgress.toFixed(2) }}%
          </p>
        </div>
      </div>

      <hr>

      <tallies :poll-data="pollData" />

      <div v-if="$store.currentkDAOHoldings !== null && kDAOHoldingsAtBlock !== null">
        <div v-if="currentVoteIsOpen">
          <hr>
          <h3 id="vote" class="has-text-weight-medium">
            <span v-if="!currentUserHasVoted">Select Your Vote</span>
            <span v-else>Thank You For Voting!</span>
          </h3>
          <p v-if="!currentUserHasVoted">
            Voting power is a snapshot of your kDAO holdings on the same block the current proposal
            went up for a vote (block <b><a target="_blank" rel="noopener" :href="`${$store.tzstatsURL}/${pollData.votingStartBlock}`">{{ pollData.votingStartBlock.toNumber().toLocaleString() }}</a></b>).
            <br><br>
            Therefore, your voting power for this vote is
            <b>{{ kDAOHoldingsAtBlock.dividedBy(Math.pow(10, 18)).toNumber().toLocaleString() }} kDAO</b> <span v-if="kDAOHoldingsAtBlock.isZero()">and you are <b>unable to vote</b></span>.
          </p>
          <p v-else>
            You cast your vote for

            <span :class="voteStates[this.voteData.voteValue.toNumber()].toLowerCase()" class="has-text-weight-bold">{{ voteStates[this.voteData.voteValue.toNumber()] }}</span>
            with <span class="has-text-weight-bold">{{ this.voteData.votes.dividedBy(Math.pow(10, 18)).toNumber().toLocaleString() }} kDAO</span>
            in block
            <a class="has-text-weight-bold" target="_blank" rel="noopener" :href="`${$store.tzstatsURL}/${$store.currentBlockHeight}`">{{ this.voteData.level.toNumber().toLocaleString() }}</a>, which was roughly <b>{{ calculateBlockTime(this.voteData.level) }}</b> ago
          </p>
          <div v-if="!currentUserHasVoted && !kDAOHoldingsAtBlock.isZero()" class="buttons is-centered">
            <button @click="vote(2)" :disabled="!currentVoteIsOpen || currentUserHasVoted || networkLoading" :class="{'is-loading': networkLoading}" class="button">Abstain</button>
            <button @click="vote(1)" :disabled="!currentVoteIsOpen || currentUserHasVoted || networkLoading" :class="{'is-loading': networkLoading}" class="button is-danger is-outlined">Vote Nay</button>
            <button @click="vote(0)" :disabled="!currentVoteIsOpen || currentUserHasVoted || networkLoading" :class="{'is-loading': networkLoading}" class="button is-primary is-outlined">Vote Yay</button>
          </div>
        </div>
        <div v-else-if="readOnly">
          <div v-if="currentUserHasVoted">
            <hr>
            <p>
              You cast your vote for

              <span :class="voteStates[this.voteData.voteValue.toNumber()].toLowerCase()" class="has-text-weight-bold">{{ voteStates[this.voteData.voteValue.toNumber()] }}</span>
              with <span class="has-text-weight-bold">{{ this.voteData.votes.dividedBy(Math.pow(10, 18)).toNumber().toLocaleString() }} kDAO</span>
              in block
              <a class="has-text-weight-bold" target="_blank" rel="noopener" :href="`${$store.tzstatsURL}/${$store.currentBlockHeight}`">{{ this.voteData.level.toNumber().toLocaleString() }}</a>, which was roughly <b>{{ calculateBlockTime(this.voteData.level) }}</b> ago
            </p>
          </div>
          <div v-else>
            <hr>
            <p class="has-text-centered">You did not participate in this vote.</p>
          </div>
        </div>
        <div v-else>
          <hr>
          <h3 class="has-text-weight-medium">Voting Is Closed</h3>
          <p>No more votes can be cast, but the vote needs to be finalized. Please finalize the vote using the button below.
            <br>
            <br>
            This will either clear the current proposal or transfer the passed proposal to the timelock where it'll be executable by the original submitter, then cancellable if some time passes and it's not executed by the original author.</p>
          <div class="buttons is-centered">
            <button
              :disabled="networkLoading"
              @click="finalizeVote"
              class="button is-primary"
              :class="{'is-loading': networkLoading}"
            >
              Finalize The Vote
            </button>
          </div>
        </div>
      </div>
      <div v-else-if="$store.walletAddress !== null">
        <div class="loader-wrapper">
          <div class="loader is-large is-primary"></div>
        </div>
      </div>
      <div v-else-if="!readOnly">
        <hr>
        <p class="has-text-centered">If you'd like to participate in voting, please connect your wallet.</p>
        <div class="buttons is-centered">
          <button @click="$eventBus.$emit('wallet-connect')" class="button is-primary">Connect Wallet</button>
        </div>
      </div>
    </div>

    <slot name="extra-footer"></slot>

  </div>
</template>

<script>
import moment from "moment";
import MichelsonEditor from "@/components/MichelsonEditor";
import axios from "axios";
import Tallies from "@/components/TallyProgress";
import BigNumber from "bignumber.js";
import Mixins from '../mixins';

export default {
  name: 'Proposal',
  props: ['pollData', 'propNumber', 'readOnly'],
  mixins: [Mixins],
  async mounted(){
    const lockupVaultAddress = this.$store.contracts.VOTING_VAULTS[this.$store.walletAddress]

    if (this.$store.wallet !== null) {
      this.voteData = lockupVaultAddress === undefined ?
                        this.pollData.voters.get(this.$store.walletAddress) :
                        this.pollData.voters.get(lockupVaultAddress)
    }
    try{
      const response = await axios.get(this.pollData.proposal.descriptionLink)
      this.pollDescription = this.$store.markdown(response.data)
      const descriptionHash = await this.hashData(response.data)
      if (await descriptionHash !== this.pollData.proposal.descriptionHash){
        this.descHashMatches = false
      }
    } catch(e) {
      this.pollDescriptionError = `Unable to fetch '${this.pollData.proposal.descriptionLink}' - ${e.toString()}`
    }

    if (this.$store.wallet !== null){
      this.kDAOHoldingsAtBlock = await this.kDAOHoldingsAtPollBlock()
    } else {
      this.kDAOHoldingsAtBlock = new BigNumber(0)
    }
  },
  components: {
    Tallies,
    MichelsonEditor
  },
  data(){
    return {
      pollDescription: null,
      pollDescriptionError: null,
      networkLoading: false,
      descHashMatches: true,
      voteData: null,
      kDAOHoldingsAtBlock: null,
      voteStates: {
        0: 'Yay',
        1: 'Nay',
        2: 'Abstain'
      }
    }
  },
  methods: {
    async hashData(message) {
      const msgUint8 = new TextEncoder().encode(message)
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    },
    calculateBlockTime(blockHeight){
      const durationSeconds = blockHeight.minus(this.$store.currentBlockHeight) * this.$store.blockTime
      // return moment.duration(durationSeconds).humanize(true)
      return moment.duration(Math.abs(durationSeconds)).format(
        'd[d] h[h] m[m] s[s]',
        {
          largest: 2
        }
      )
    },
    async vote(voteSelection){
      this.networkLoading = true
      const lockupVaultAddress = this.$store.contracts.VOTING_VAULTS[this.$store.walletAddress]

      let sendResult
      try{
        const contract = lockupVaultAddress === undefined ?
                          await this.$store.tezosToolkit.wallet.at(this.$store.contracts.DAO) :
                          await this.$store.tezosToolkit.wallet.at(lockupVaultAddress)

        sendResult = await contract.methods.vote(voteSelection).send()
        this.$eventBus.$emit('tx-submitted', sendResult)
        await sendResult.confirmation(1)

        this.$store.daoParameters = null
        this.$eventBus.$emit('update-dao-info')
      } catch(e) {
        console.log("Error voting!")
        console.error(e)
      } finally {
        this.$eventBus.$emit('tx-finished', sendResult)
        this.networkLoading = false
      }
    },
    async finalizeVote(){
      this.networkLoading = true
      let sendResult
      try{
        const daoContract = await this.$store.tezosToolkit.wallet.at(this.$store.contracts.DAO)
        sendResult = await daoContract.methods.endVoting(null).send()
        this.$eventBus.$emit('tx-submitted', sendResult)
        await sendResult.confirmation(1)

        this.$store.daoParameters = null
        this.$eventBus.$emit('update-dao-info')
      } catch(e) {
        console.log("Error cancelling!")
        console.error(e)
      } finally {
        this.$eventBus.$emit('tx-finished', sendResult)
        this.networkLoading = false
      }
    },
    async kDAOHoldingsAtPollBlock(){
      const kDAOTokenStorage = this.$store.kDAOTokenContractStorage

      const lockupVaultAddress = this.$store.contracts.VOTING_VAULTS[this.$store.walletAddress]

      let address = lockupVaultAddress === undefined ? this.$store.walletAddress : lockupVaultAddress

      if (this.$store.wallet === null || this.$store.kDAOCheckpointCount.isZero()){
        return new BigNumber(0)
      }

      let checkpoint = null
      let cursor = this.$store.kDAOCheckpointCount.minus(1)
      let cursorValue = await kDAOTokenStorage.checkpoints.get({0: address, 1: cursor})

      // Check if we even have to search by seeing if the latest snapshot is before the voting snapshot block
      if (cursorValue.fromBlock.isLessThanOrEqualTo(this.pollData.votingStartBlock)){
        return cursorValue.balance
      }

      while(cursor.isGreaterThanOrEqualTo(1)){
        const cursorValue = await kDAOTokenStorage.checkpoints.get({0: address, 1: cursor})
        const previousValue = await kDAOTokenStorage.checkpoints.get({0: address, 1: cursor.minus(1)})

        if (previousValue.fromBlock.isLessThanOrEqualTo(this.pollData.votingStartBlock) &&
              cursorValue.fromBlock.isGreaterThanOrEqualTo(this.pollData.votingStartBlock)){
          checkpoint = previousValue
          break
        }
        cursor = cursor.minus(1)
      }

      // If we've exhausted all checkpoints and the vote happened before the first checkpoint was taken
      // then the holdings were 0
      if (checkpoint === null){
        return new BigNumber(0)
      }

      return checkpoint.balance
    }
  },
  computed:{
    currentVoteIsOpen(){
      return this.$store.currentBlockHeight < this.pollData.votingEndBlock
    },
    currentVoteStatus(){
      if (this.currentVoteIsOpen) {
        return "OPEN"
      } else {
        return "CLOSED"
      }
    },
    quorumHasBeenMet(){
      return this.pollData.totalVotes.isGreaterThanOrEqualTo(this.pollData.quorum)
    },
    currentUserHasVoted(){
      if (!this.voteData){
        return false
      }
      return true
    },
    quorumProgress(){
      const progress = this.pollData.totalVotes.dividedBy(this.pollData.quorum).times(100)
      if (progress.isGreaterThanOrEqualTo(100)){
        return 100
      }
      return progress
    },
  }
}
</script>

<style type="text/scss" lang="scss">
@import '../assets/sass/globals';
.proposal{
  .control{
    max-width: 100%;
  }
  .proposal-title{
    margin-top: 0 !important;
  }
  .proposal-description{
    overflow: auto;
    border: 2px solid #f5f5f5;
    border-radius: 5px;
    padding: 1rem;
  }
  .voting-info{
    span{
      &.yay{
        color: $primary;
      }
      &.nay{
        color: $danger;
      }
      &.abstain{
        color: $text-light;
      }
    }
    h4{
      margin-top: 1rem;
    }
    .progress-footer{
      padding: 0.5rem 0.25rem;
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
